import { useOrganization, useUser } from '@clerk/clerk-react';
import { Loader2, LockKeyhole } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { getStageItemsByStageId } from '@/actions/stage-items/getStageItemsByStageId';
import DefaultCompanyLogo from '@/assets/icons/default-company-logo.svg?react';
import TimelineSvg from '@/assets/icons/timeline.svg?react';
import { StageItemCard } from '@/components/discovery/StageItemCard';
import { LikedTerritoriesByState } from '@/components/territories/LikedTerritoriesByState';
import { Progress } from '@/components/ui/progress';
import { HOME_TOUR_CLASSES } from '@/constants/tour/classNames';
import { toast } from '@/hooks/ui/use-toast';
import { useGetZorGeneralSetting } from '@/hooks/zor-general-setting/useGetZorGeneralSetting';
import { calculateDaysBetween } from '@/lib/date';
import {
  getCurrentStage,
  getStageItemStatus,
  isLockedFunction,
} from '@/lib/discovery';
import { useStageItemStore } from '@/stores/stages/useStageItemStore';
import { useStageStore } from '@/stores/stages/useStageStore';
import { useZorGeneralSettingStore } from '@/stores/useZorGeneralSettingStore';
import { DiscoveryStatus } from '@/types/discovery.status.type';
import { StageItem } from '@/types/stage.item.type';
import { Stage } from '@/types/stage.type';

const DashboardHome = () => {
  const { organization } = useOrganization();
  const { user } = useUser();
  const organizationName = organization?.name || '';
  const location = useLocation();

  const { stageList, featureLocks, discoveryStatus } = useStageStore();
  const { stageItems: stageItemsFromStore, setStageItems } =
    useStageItemStore();

  // ZorGeneralSetting data
  const { result: zorGeneralSettingResult, isLoading: isLoadingZorSettings } =
    useGetZorGeneralSetting();
  const { zorGeneralSetting, setZorGeneralSetting } =
    useZorGeneralSettingStore();

  // Set ZorGeneralSetting data to store
  useEffect(() => {
    if (zorGeneralSettingResult?.data) {
      setZorGeneralSetting(zorGeneralSettingResult.data);
    }
  }, [zorGeneralSettingResult, setZorGeneralSetting]);

  useEffect(() => {
    const state = location.state as {
      from?: string;
      message?: string;
      feature?: string;
    } | null;

    if (state?.message && state?.feature) {
      toast({
        title: 'Feature Locked',
        description: state.message,
        variant: 'destructive',
      });

      window.history.replaceState(null, '', location.pathname);
    }
  }, [location.state, location.pathname]);

  // Dynamic timeline info based on ZorGeneralSetting and user creation date
  const timelineInfo = useMemo(() => {
    const discoveryCountdown = zorGeneralSetting?.onboarding_countdown || 210;

    if (!user?.createdAt) {
      // Fallback if user creation date is not available
      return {
        days: discoveryCountdown,
        label: `${discoveryCountdown} Days`,
        subLabel: 'Estimated Timeline To Open',
        isOverdue: false,
      };
    }

    const daysPassed = calculateDaysBetween(user.createdAt);
    const remainingDays = discoveryCountdown - daysPassed;
    // const isOverdue = remainingDays < 0;
    const isOverdue = false;

    if (isOverdue) {
      const overdueDays = Math.abs(remainingDays);
      return {
        days: remainingDays,
        label: `${overdueDays} Days Overdue`,
        subLabel: 'Discovery Period Exceeded',
        isOverdue: true,
      };
    }

    return {
      days: remainingDays,
      label: `${remainingDays <= 0 ? 0 : remainingDays} Days`,
      subLabel: 'Remaining To Complete Discovery',
      isOverdue: false,
    };
  }, [zorGeneralSetting, user?.createdAt]);

  // Dynamic greeting text
  const greetingText = useMemo(() => {
    if (!zorGeneralSetting?.homepage_greeting_text) {
      return `Here's an overview of your ${organizationName} discovery progress.`;
    }

    const firstName = user?.firstName || 'there';
    return zorGeneralSetting.homepage_greeting_text.replace(
      '{{first_name}}',
      firstName
    );
  }, [zorGeneralSetting, user?.firstName, organizationName]);

  const currentStage = useMemo(
    () => getCurrentStage(stageList, discoveryStatus),
    [stageList, discoveryStatus]
  );

  const { isLoading, isError, data } = useStageItems(currentStage?.id);

  useEffect(() => {
    if (isError) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again later',
        variant: 'destructive',
      });
    }
  }, [isError]);

  useEffect(() => {
    if (!data) return;

    const sortedStageItems = sortStageItems(data);
    setStageItems(sortedStageItems);
  }, [data, setStageItems]);

  const { completedItemsCount, totalItemsCount, completionPercentage } =
    useMemo(() => {
      const completed =
        stageItemsFromStore?.filter((item) =>
          discoveryStatus?.completed_stage_item_ids.includes(item.id ?? '')
        ).length || 0;

      const total = stageItemsFromStore?.length || 0;

      return {
        completedItemsCount: completed,
        totalItemsCount: total,
        completionPercentage: total > 0 ? (completed / total) * 100 : 0,
      };
    }, [stageItemsFromStore, discoveryStatus]);

  const { activeItemIndex, activeItem } = useMemo(() => {
    const index = stageItemsFromStore?.findIndex(
      (_, idx) =>
        getStageItemStatus(
          idx,
          currentStage?.id ?? '',
          stageItemsFromStore,
          discoveryStatus
        ) === 'active'
    );

    return {
      activeItemIndex: index,
      activeItem:
        index !== -1 ? stageItemsFromStore[index] : stageItemsFromStore?.[0],
    };
  }, [stageItemsFromStore, currentStage, discoveryStatus]);

  const isTerritoriesLocked = useMemo(
    () =>
      isLockedFunction('territories', stageList, featureLocks, discoveryStatus),
    [stageList, featureLocks, discoveryStatus]
  );

  const territoriesUnlockStage = useMemo(
    () =>
      featureLocks?.territories
        ? stageList.find((s) => s.id === featureLocks?.territories)
        : undefined,
    [featureLocks, stageList]
  );

  return (
    <div className="space-y-6">
      <Header
        organization={{
          imageUrl:
            zorGeneralSetting?.company_logo_url || organization?.imageUrl,
        }}
        organizationName={organizationName}
        greetingText={greetingText}
        timelineInfo={timelineInfo}
        isLoadingZorSettings={isLoadingZorSettings}
      />

      {timelineInfo.isOverdue && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <LockKeyhole className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h3 className="as-title-03 as-strong text-red-800">
                Discovery Period Exceeded
              </h3>
              <p className="as-body-03 mt-1 text-red-700">
                Your discovery timeline has exceeded by{' '}
                {Math.abs(timelineInfo.days)} days. Please complete the
                remaining steps to activate your account and start operations.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className={`${HOME_TOUR_CLASSES.CURRENT_DISCOVERY_STEP} space-y-4`}>
        {currentStage && (
          <StageHeader
            currentStage={currentStage}
            isLoading={isLoading}
            completedItemsCount={completedItemsCount}
            totalItemsCount={totalItemsCount}
            completionPercentage={completionPercentage}
          />
        )}

        <StageContent
          isLoading={isLoading}
          currentStage={currentStage}
          stageItemsFromStore={stageItemsFromStore}
          activeItemIndex={activeItemIndex}
          activeItem={activeItem}
          discoveryStatus={discoveryStatus}
        />
      </div>

      <div
        className={`${HOME_TOUR_CLASSES.LIKED_TERRITORIES} w-full space-y-4`}
      >
        <h5 className="as-strong">Your Territories</h5>
        {isTerritoriesLocked ? (
          <div className="flex items-center justify-center">
            <div className="as-title-03 as-strong flex flex-row items-center gap-3 text-typo-gray-tertiary">
              <LockKeyhole className="h-4 w-4" />{' '}
              <p>
                Currently Locked
                {territoriesUnlockStage
                  ? ` - Unlocked at Stage ${territoriesUnlockStage.index} ${territoriesUnlockStage.name}`
                  : ''}
              </p>
            </div>
          </div>
        ) : (
          <LikedTerritoriesByState />
        )}
      </div>
    </div>
  );
};

const Header = ({
  organization,
  organizationName,
  greetingText,
  timelineInfo,
  isLoadingZorSettings,
}: {
  organization: {
    imageUrl?: string;
  };
  organizationName: string;
  greetingText: string;
  timelineInfo: {
    days: number;
    label: string;
    subLabel: string;
    isOverdue: boolean;
  };
  isLoadingZorSettings: boolean;
}) => (
  <div className="flex items-center justify-between gap-6 max-lg:flex-col">
    <div className="flex items-start gap-5">
      {organization?.imageUrl ? (
        <img
          src={organization.imageUrl}
          alt={organizationName || 'Company logo'}
          className="h-[60px] w-[60px] rounded-full border-2 border-white shadow-lg lg:h-[90px] lg:w-[90px]"
        />
      ) : (
        <DefaultCompanyLogo className="h-[60px] w-[60px] rounded-full border-2 border-white shadow-lg lg:h-[90px] lg:w-[90px]" />
      )}
      <div className="space-y-1">
        <h2 className="as-strong">Welcome to {organizationName}</h2>
        <p className="as-body-02 text-muted-foreground">
          {isLoadingZorSettings ? 'Loading...' : greetingText}
        </p>
      </div>
    </div>
    {timelineInfo?.days && timelineInfo?.days > 0 && (
      <div
        className={`${HOME_TOUR_CLASSES.ESTIMATED_TIMELINE} flex flex-shrink-0 items-center gap-5 max-lg:w-full max-lg:rounded-lg max-lg:bg-green-50 max-lg:p-4`}
      >
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
            timelineInfo.isOverdue ? 'bg-red-100' : 'bg-green-100'
          } max-lg:h-12 max-lg:w-12`}
        >
          <TimelineSvg className="max-lg:h-6 max-lg:w-6" />
        </div>
        <div>
          <p
            className={`as-title-03 as-strong ${
              timelineInfo.isOverdue ? 'text-red-600' : ''
            }`}
          >
            {isLoadingZorSettings ? 'Loading...' : timelineInfo.label}
          </p>
          <p
            className={`as-title-04 as-strong ${
              timelineInfo.isOverdue ? 'text-red-500' : 'text-muted-foreground'
            }`}
          >
            {timelineInfo.subLabel}
          </p>
        </div>
      </div>
    )}
  </div>
);

const StageHeader = ({
  isLoading,
  completedItemsCount,
  totalItemsCount,
  completionPercentage,
}: {
  currentStage?: Stage;
  isLoading: boolean;
  completedItemsCount: number;
  totalItemsCount: number;
  completionPercentage: number;
}) => (
  <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
    <h5 className="as-strong">Complete the next step to continue:</h5>
    {!isLoading && (
      <div className="flex items-center gap-2">
        <p className="as-title-03 as-strong flex-shrink-0 text-typo-gray-tertiary">
          {`${completedItemsCount} / ${totalItemsCount} completed`}
        </p>
        <div className="w-40">
          <Progress value={completionPercentage} className="bg-muted" />
        </div>
      </div>
    )}
  </div>
);

const StageContent = ({
  isLoading,
  currentStage,
  stageItemsFromStore,
  activeItemIndex,
  activeItem,
  discoveryStatus,
}: {
  isLoading: boolean;
  currentStage?: Stage;
  stageItemsFromStore: StageItem[];
  activeItemIndex: number;
  activeItem: StageItem;
  discoveryStatus?: DiscoveryStatus;
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  if (!currentStage) {
    return (
      <div className="flex items-center justify-center">
        <p className="as-title-03 as-strong text-typo-gray-tertiary">
          You've completed all the discovery stages.
        </p>
      </div>
    );
  }

  if (!stageItemsFromStore?.length) {
    return (
      <div className="flex items-center justify-center">
        <p className="as-title-03 as-strong text-typo-gray-tertiary">
          No stage items found
        </p>
      </div>
    );
  }

  if (activeItemIndex !== -1) {
    return (
      <StageItemCard
        index={activeItemIndex}
        status={getStageItemStatus(
          activeItemIndex,
          currentStage?.id ?? '',
          stageItemsFromStore,
          discoveryStatus
        )}
        stageItem={activeItem}
      />
    );
  }

  return (
    <div className="flex items-center justify-center">
      <p className="as-title-03 as-strong text-typo-gray-tertiary">
        Something went wrong with your discovery progress. Please contact the
        team.
      </p>
    </div>
  );
};

const useStageItems = (stageId?: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [data, setData] = useState<StageItem[] | null>(null);

  useEffect(() => {
    if (!stageId) return;

    setIsLoading(true);
    setIsError(false);

    getStageItemsByStageId(stageId)
      .then((res) => setData(res.data))
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false));
  }, [stageId]);

  return { isLoading, isError, data };
};

const sortStageItems = (items: StageItem[]): StageItem[] => {
  return items.reduce<StageItem[]>((acc, curr) => {
    if (curr.previous_stage_item_id !== null) return acc;

    acc.push(curr);
    let nextItemId = curr.id;

    while (nextItemId) {
      const nextItem = items.find(
        (item) => item.previous_stage_item_id === nextItemId
      );
      if (!nextItem) break;

      acc.push(nextItem);
      nextItemId = nextItem.id;
    }

    return acc;
  }, []);
};

export { DashboardHome };
